import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

// REPARTO DE RESIDENTES: cada supervisora hace todos sus pisos en un ciclo de 2 semanas (10 días hábiles).
// Sale ya repartido por defecto (mismo nº de pisos cada día); aquí solo se corrige: se pulsa el círculo del día
// (semana A arriba, semana B abajo) y se guardan los cambios. Mismo formato que «Organizar Ruta 3».

const DIAS = [
  { l: "L", largo: "lunes", color: "#2563eb" },
  { l: "M", largo: "martes", color: "#7c3aed" },
  { l: "X", largo: "miércoles", color: "#0d9488" },
  { l: "J", largo: "jueves", color: "#ea580c" },
  { l: "V", largo: "viernes", color: "#db2777" },
];
const CICLO = Array.from({ length: 10 }, (_, i) => ({ n: i + 1, sem: i < 5 ? "A" : "B", ...DIAS[i % 5] }));
const cic = (n) => CICLO[n - 1];

const AdminResidentesPlan = () => {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [cambios, setCambios] = useState({});            // id_piso -> día (sin guardar)
  const [error, setError] = useState("");
  const [aviso, setAviso] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [supSel, setSupSel] = useState("todas");
  const [busca, setBusca] = useState("");
  const [filtroDia, setFiltroDia] = useState(null);       // null | 1..10

  const cargar = useCallback(() => {
    setError("");
    axios.get("/api/tareas/residentes/config").then((r) => { setD(r.data); setCambios({}); }).catch((e) => setError(e.response?.data?.error || "No se pudo cargar el reparto."));
  }, []);
  useEffect(() => { cargar(); }, [cargar]);
  useEffect(() => {
    if (!aviso) return undefined;
    const t = setTimeout(() => setAviso(""), 6000);
    return () => clearTimeout(t);
  }, [aviso]);

  const pisos = useMemo(() => (d ? d.supervisoras.flatMap((s) => s.pisos.map((p) => ({ ...p, sup: s.user }))) : []), [d]);
  const diaDe = (p) => (cambios[p.id] != null ? cambios[p.id] : p.dia_ciclo);
  const supervisoras = useMemo(() => (d ? d.supervisoras.map((s) => s.user).sort((a, b) => a.localeCompare(b, "es")) : []), [d]);
  const base = useMemo(() => pisos.filter((p) => supSel === "todas" || p.sup === supSel), [pisos, supSel]);
  const conteo = useMemo(() => {
    const c = Array(11).fill(0);
    base.forEach((p) => { const n = diaDe(p); if (n) c[n] += 1; });
    return c;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, cambios]);
  const filas = useMemo(() => {
    const q = busca.trim().toLowerCase();
    return base.filter((p) => (!q || `${p.abreviatura} ${p.asistenta || ""} ${p.sup}`.toLowerCase().includes(q)) && (filtroDia == null || diaDe(p) === filtroDia));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base, busca, filtroDia, cambios]);
  // Agrupado por el día GUARDADO (no salta de grupo mientras editas)
  const grupos = useMemo(() => CICLO.map((c) => ({
    c, filas: filas.filter((p) => p.dia_ciclo === c.n).sort((a, b) => a.sup.localeCompare(b.sup, "es") || String(a.abreviatura).localeCompare(String(b.abreviatura), "es", { numeric: true })),
  })).filter((g) => g.filas.length), [filas]);

  const elegir = (p, n) => setCambios((c) => {
    const resto = { ...c };
    if (n === p.dia_ciclo) delete resto[p.id]; else resto[p.id] = n;
    return resto;
  });
  const nCambios = Object.keys(cambios).length;

  const guardar = async () => {
    if (!nCambios) return;
    setGuardando(true); setError("");
    try {
      const { data } = await axios.post("/api/tareas/residentes/config/guardar", { cambios: Object.entries(cambios).map(([id, dia]) => ({ id_piso: Number(id), dia_ciclo: dia })) });
      setAviso(`✅ Guardado: ${data.guardados} piso${data.guardados === 1 ? "" : "s"} cambiado${data.guardados === 1 ? "" : "s"}.`);
      cargar();
    } catch (e) { setError(e.response?.data?.error || "No se pudo guardar."); }
    finally { setGuardando(false); }
  };
  const repartirDeNuevo = async () => {
    if (supSel === "todas" || !window.confirm(`Se rehará el reparto de ${supSel} desde cero (se pierden sus cambios manuales). ¿Seguir?`)) return;
    setGuardando(true);
    try { await axios.post("/api/tareas/residentes/config/auto", { supervisora: supSel }); setAviso(`✅ Reparto de ${supSel} rehecho.`); cargar(); }
    catch (e) { setError(e.response?.data?.error || "No se pudo repartir."); }
    finally { setGuardando(false); }
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f1f5f9", fontFamily: "Segoe UI, sans-serif", paddingBottom: nCambios ? 90 : 20 }}>
      <div style={{ background: "#1e3a5f", color: "#fff", padding: "14px 18px", display: "flex", alignItems: "center", gap: 12 }}>
        <button onClick={() => navigate(-1)} style={st.btnCab}>← Volver</button>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, opacity: 0.7 }}>Reportes · residentes · ciclo de 2 semanas</div>
          <strong style={{ fontSize: 20 }}>🧑‍🎓 Reparto de pisos por día</strong>
        </div>
      </div>

      <div style={{ padding: "14px 16px", maxWidth: 1000, margin: "0 auto" }}>
        <div style={{ background: "#f5f3ff", border: "1px solid #ddd6fe", color: "#5b21b6", borderRadius: 12, padding: "10px 14px", fontSize: 13.5, marginBottom: 12 }}>
          Cada supervisora hace todos sus pisos en <b>2 semanas</b> (10 días). El reparto <b>ya viene hecho por defecto</b> (mismo número de pisos al día): solo tienes que
          pulsar otro día si quieres cambiar un piso. Fila de arriba = <b>semana A</b>, fila de abajo = <b>semana B</b>.
          {d && <> Hoy es el día <b>{cic(d.dia_ciclo_hoy).sem} · {cic(d.dia_ciclo_hoy).largo}</b> (la semana A empieza el {d.ciclo_inicio.slice(8, 10)}/{d.ciclo_inicio.slice(5, 7)}).</>}
        </div>
        {error && <div style={{ background: "#fee2e2", color: "#991b1b", borderRadius: 10, padding: 12, marginBottom: 10 }}>{error}</div>}
        {aviso && <div style={{ background: "#dcfce7", color: "#166534", borderRadius: 10, padding: 12, marginBottom: 10, fontWeight: 700 }}>{aviso}</div>}

        {/* Pisos por día del ciclo (pulsa para FILTRAR) */}
        {["A", "B"].map((sem) => (
          <div key={sem} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
            {CICLO.filter((c) => c.sem === sem).map((c) => (
              <button key={c.n} onClick={() => setFiltroDia(filtroDia === c.n ? null : c.n)} title={`Ver solo los pisos de la semana ${c.sem}, ${c.largo}`}
                style={{ ...st.diaCard, borderColor: filtroDia === c.n ? c.color : "#e2e8f0", background: filtroDia === c.n ? `${c.color}14` : "#fff", outline: d && d.dia_ciclo_hoy === c.n ? "2px dashed #94a3b8" : "none", outlineOffset: 2 }}>
                <span style={{ fontSize: 12, fontWeight: 800, color: c.color, textTransform: "capitalize" }}>{sem} · {c.largo}</span>
                <span style={{ fontSize: 24, fontWeight: 900, color: "#1e293b" }}>{d ? conteo[c.n] : "…"}</span>
              </button>
            ))}
          </div>
        ))}

        <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", margin: "6px 0 10px" }}>
          <select value={supSel} onChange={(e) => setSupSel(e.target.value)} style={st.select}>
            <option value="todas">👩‍💼 Todas las supervisoras</option>
            {supervisoras.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <input value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="🔍 Buscar piso o asistenta…"
            style={{ flex: 1, minWidth: 190, padding: "9px 12px", borderRadius: 12, border: "2px solid #c7d2fe", fontSize: 13.5, outline: "none" }} />
          {supSel !== "todas" && <button onClick={repartirDeNuevo} disabled={guardando} style={{ ...st.chip, borderColor: "#c4b5fd", color: "#5b21b6" }}>⚡ Repartir de nuevo</button>}
          {filtroDia != null && <button onClick={() => setFiltroDia(null)} style={{ ...st.chip, borderColor: "#fca5a5", color: "#b91c1c" }}>✕ Quitar filtro</button>}
        </div>

        {!d ? <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>Cargando…</div> : (
          <div style={{ background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", overflow: "hidden" }}>
            {filas.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>No hay pisos con estos filtros.</div>}
            {grupos.map((g) => (
              <div key={g.c.n}>
                <div style={{ position: "sticky", top: 0, zIndex: 1, background: `${g.c.color}12`, color: g.c.color, fontWeight: 900, padding: "7px 14px", borderBottom: "1px solid #e2e8f0", textTransform: "capitalize", fontSize: 14 }}>
                  Semana {g.c.sem} · {g.c.largo} <span style={{ fontWeight: 600, opacity: 0.8, textTransform: "none" }}>· {g.filas.length} piso{g.filas.length !== 1 ? "s" : ""}</span>
                </div>
                {g.filas.map((p, i) => {
                  const cambiado = cambios[p.id] != null;
                  const dd = diaDe(p);
                  return (
                    <div key={p.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "9px 14px", background: cambiado ? "#fffbeb" : i % 2 ? "#fafafa" : "#fff", borderBottom: "1px solid #f1f5f9" }}>
                      <div style={{ flex: "1 1 250px", minWidth: 200 }}>
                        <div style={{ fontWeight: 800, color: "#1e293b", overflowWrap: "anywhere" }}>{p.abreviatura}</div>
                        <div style={{ fontSize: 11.5, color: "#94a3b8" }}>{supSel === "todas" ? `${p.sup} · ` : ""}{p.asistenta || "—"}</div>
                        <div style={{ fontSize: 12, color: "#475569", marginTop: 3 }}>
                          <span title="Alumnos del piso: A / R" style={st.chipAR}>👥 <b style={{ color: "#1d4ed8" }}>A {p.alumnos.a}</b> · <b style={{ color: "#b45309" }}>R {p.alumnos.r}</b></span>
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {["A", "B"].map((sem) => (
                          <div key={sem} style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
                            <span style={{ width: 14, fontSize: 11, fontWeight: 800, color: "#94a3b8" }}>{sem}</span>
                            {CICLO.filter((c) => c.sem === sem).map((c) => {
                              const sel = dd === c.n;
                              return (
                                <button key={c.n} onClick={() => elegir(p, c.n)} title={`Semana ${c.sem} · ${c.largo}`} style={{
                                  width: 34, height: 34, borderRadius: "50%", fontWeight: 900, fontSize: 13, cursor: "pointer",
                                  border: `2px solid ${sel ? c.color : "#cbd5e1"}`, background: sel ? c.color : "#fff", color: sel ? "#fff" : "#94a3b8",
                                  boxShadow: cambiado && sel ? "0 0 0 3px #fde68a" : "none",
                                }}>{c.l}</button>
                              );
                            })}
                          </div>
                        ))}
                      </div>
                      <div style={{ flex: "1 1 130px", fontSize: 12, color: cambiado ? "#b45309" : "#94a3b8", minWidth: 110 }}>
                        {cambiado ? `Sin guardar · ahora: ${cic(p.dia_ciclo).sem} ${cic(p.dia_ciclo).largo}` : "Guardado"}
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>

      {nCambios > 0 && (
        <div style={{ position: "fixed", left: 0, right: 0, bottom: "var(--fichaje-h, 0px)", zIndex: 100, background: "#1e293b", color: "#fff", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, justifyContent: "center", flexWrap: "wrap", boxShadow: "0 -4px 16px rgba(0,0,0,0.2)" }}>
          <strong>{nCambios} cambio{nCambios !== 1 ? "s" : ""} sin guardar</strong>
          <button onClick={() => setCambios({})} disabled={guardando} style={{ ...st.btnCab, background: "rgba(255,255,255,0.15)" }}>Descartar</button>
          <button onClick={guardar} disabled={guardando} style={{ border: "none", borderRadius: 10, padding: "10px 22px", fontWeight: 900, fontSize: 15, cursor: "pointer", background: "#22c55e", color: "#fff", opacity: guardando ? 0.6 : 1 }}>
            {guardando ? "Guardando…" : "💾 Guardar cambios"}
          </button>
        </div>
      )}
    </div>
  );
};

const st = {
  btnCab: { background: "rgba(255,255,255,0.15)", border: "none", borderRadius: 8, color: "#fff", fontWeight: 700, cursor: "pointer", padding: "8px 14px" },
  diaCard: { display: "flex", flexDirection: "column", alignItems: "flex-start", gap: 2, minWidth: 104, padding: "8px 14px", borderRadius: 14, cursor: "pointer", border: "2px solid #e2e8f0", background: "#fff" },
  select: { padding: "9px 12px", borderRadius: 12, border: "2px solid #c7d2fe", fontSize: 13.5, fontWeight: 700, color: "#3730a3", background: "#fff", cursor: "pointer", outline: "none" },
  chip: { border: "2px solid #c7d2fe", background: "#fff", color: "#3730a3", borderRadius: 999, padding: "6px 12px", fontSize: 12.5, fontWeight: 800, cursor: "pointer" },
  chipAR: { fontSize: 11.5, fontWeight: 700, color: "#334155", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 999, padding: "1px 9px", whiteSpace: "nowrap" },
};

export default AdminResidentesPlan;
